import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import React, { useEffect, useState, useRef } from "react";
import moment from "moment";
import { FiSearch } from "react-icons/fi";
import { FaPlane, FaCalendarAlt, FaClock, FaTicketAlt } from "react-icons/fa";
import { AiOutlineClose } from "react-icons/ai";
import initializeDepartureDate from "../../libs/initializeDepartureDate";
import initializeArrivalDate from "../../libs/initializeArrivalDate";
import { useLocation, useSearchParams } from "react-router-dom";
import { nextPage } from "../../config/nextPage";
import cookieHandler from "../../libs/cookieHandler";
import times from "../../libs/times";
import { getStoredPromoCode } from "../../libs/tracking";

// Import custom PNG/WebP images for inputs
import dateIcon from "../../images/date.webp";
import timeIcon from "../../images/time.webp";
import promoIcon from "../../images/promo-code.webp";
import searchIcon from "../../images/search.webp";

export default function AirportParkingSearch({
  open,
  setOpen,
  refer,
  airports,
  defaultAirport,
}) {
  const [selectOpen, setSelectOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const highlightedItemRef = useRef(null);
  const [params] = useSearchParams();
  const location = useLocation();
  const pathname = location.pathname;
  const getDeparture = params.get("departure");
  const getArrival = params.get("arrival");
  const getPromocode = params.get("promocode");
  const getAirport = params.get("airport");
  const singleProduct = params.get("product");
  const getCustomCid = params.get("cid");
  const [state, setState] = useState("");
  const [validation, setValidation] = useState("");
  const [promocode, setPromoCode] = useState(getPromocode || getStoredPromoCode() || "");
  const [airport, setAirport] = useState(getAirport || "" || defaultAirport);
  const [selectedAirportLabel, setSelectedAirportLabel] = useState("");
  const [departure, setDeparture] = useState(
    initializeDepartureDate(getDeparture || "")
  );
  const [arrival, setArrival] = useState(
    initializeArrivalDate(getArrival || "")
  );

  const pickerRef = useRef(null);
  const dropdownRef = useRef(null);
  const arrivalPickerRef = useRef(null);

  const setDepartureDate = (date) => {
    let depTime = moment(departure).format("HH:mm");
    let depDate = moment(date).format("yyyy-MM-DD");
    setDeparture(moment(`${depDate} ${depTime}`).format("yyyy-MM-DD HH:mm"));

    // Automatically set arrival date to 7 days after drop-off date
    let arrTime = moment(arrival).format("HH:mm");
    let arrDate = moment(date).add(7, "days").format("yyyy-MM-DD");
    setArrival(moment(`${arrDate} ${arrTime}`).format("yyyy-MM-DD HH:mm"));
  };

  const setArrivalDate = (date) => {
    setArrival(moment(date).format("yyyy-MM-DD HH:mm"));
  };

  let airportsUpdate = [];

  //@ts-ignore
  if (airports?.length) {
    //@ts-ignore
    airports?.map((airport) =>
      airportsUpdate.push({
        label: airport?.name,
        value: airport?.code,
        terminal: airport.terminal,
      })
    );
  }

  const setTime = (time, mode) => {
    const [h, m] = time.split(":").map((v) => parseInt(v, 10));

    if (mode === "arrival") {
      setArrival(
        moment(arrival)
          .set("hours", h)
          .set("minutes", m)
          .format("yyyy-MM-DD HH:mm")
      );
    } else {
      setDeparture(
        moment(departure)
          .set("hours", h)
          .set("minutes", m)
          .format("yyyy-MM-DD HH:mm")
      );
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setValidation("");

    if (!airport) {
      setValidation("Please select an airport to continue");
      setState("error");
      return;
    }
    const params = new URLSearchParams({
      departure: departure,
      arrival: arrival,
      promocode: promocode,
      airport,
      product: singleProduct,
    });

    setState("loading");
    nextPage(`${refer}?${params}`);
    setOpen(false);
    setTimeout(() => setState("initial"), 2000);
    if (pathname === refer) {
      setState("");
    }
  };

  const ref = useRef(null);
  // const isView = useInView(ref);

  // useEffect(() => {
  //   if (ref.current) {
  //     if (isView) {
  //       setTimeout(() => {
  //         //@ts-ignore
  //         ref.current.classList.add("visible");
  //       }, 200);
  //     } else {
  //       //@ts-ignore
  //       ref.current.classList.remove("visible");
  //     }
  //   }
  // }, [isView]);

  useEffect(() => {
    if (airport?.length) {
      setValidation("");
    }
  }, [airport]);

  useEffect(() => {
    cookieHandler(getCustomCid);
  }, []);

  // Check if device is mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 577); // sm breakpoint
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);

    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setSelectOpen(false);
      }
    };

    if (selectOpen) {
      document.addEventListener("mousedown", handleOutsideClick);
    } else {
      document.removeEventListener("mousedown", handleOutsideClick);
    }

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [selectOpen]);

  // Filter airports based on search term
  const filteredAirports = airportsUpdate.filter((airport) =>
    airport.label.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Handle airport selection
  const handleAirportSelect = (airport) => {
    setAirport(airport.value);
    setSelectedAirportLabel(airport.label);
    setValidation("");
    setSelectOpen(false);
    setSearchTerm("");
    setHighlightedIndex(-1);
  };

  // Keyboard navigation handler
  const handleKeyDown = (e) => {
    if (!selectOpen) return;

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        if (filteredAirports.length > 0) {
          setHighlightedIndex((prev) =>
            prev < filteredAirports.length - 1 ? prev + 1 : prev
          );
        }
        break;
      case "ArrowUp":
        e.preventDefault();
        if (filteredAirports.length > 0) {
          setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : 0));
        }
        break;
      case "Enter":
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < filteredAirports.length) {
          handleAirportSelect(filteredAirports[highlightedIndex]);
        }
        break;
      case "Escape":
        e.preventDefault();
        setSelectOpen(false);
        setSearchTerm("");
        setHighlightedIndex(-1);
        break;
      default:
        // Type to search - capture any alphanumeric key
        if (e.key.length === 1 && /[a-zA-Z0-9\s]/.test(e.key)) {
          e.preventDefault();
          setSearchTerm((prev) => prev + e.key);
          setHighlightedIndex(0);
        } else if (e.key === "Backspace") {
          e.preventDefault();
          setSearchTerm((prev) => prev.slice(0, -1));
          setHighlightedIndex(0);
        }
        break;
    }
  };

  useEffect(() => {
    if (isMobile && pickerRef.current !== null) {
      pickerRef.current.input.readOnly = true;
    }
    if (arrivalPickerRef.current !== null) {
      arrivalPickerRef.current.input.readOnly = true;
    }
  }, [isMobile, pickerRef]);

  // Reset highlighted index when search term changes
  useEffect(() => {
    setHighlightedIndex(-1);
  }, [searchTerm]);

  // Auto-scroll to highlighted item
  useEffect(() => {
    if (highlightedItemRef.current) {
      highlightedItemRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      });
    }
  }, [highlightedIndex]);

  // Set selected airport label on mount
  useEffect(() => {
    if (airport && airportsUpdate.length > 0) {
      const found = airportsUpdate.find((a) => a.value === airport);
      if (found) {
        setSelectedAirportLabel(found.label);
      }
    }
  }, [airport, airportsUpdate]);

  return (
    <div className="w-full font-poppins sm:mt-10 mt-5 md:px-4 max-w-[1400px] mx-auto animate-slide-in-up">
      {/* Tabs Section */}
      <div className="w-full md:w-[50%] bg-brandLime pt-4 pb-4 px-6 md:px-8 rounded-t-[20px] flex items-center justify-between shadow-lg border-b-0 relative z-0 translate-y-[1px]">
        {[
          { label: "Parking", icon: FaPlane },
          { label: "Meet & Greet", icon: FaPlane },
          { label: "Park & Ride", icon: FaPlane }
        ].map((tab, idx) => (
          <React.Fragment key={idx}>
            <div className="flex items-center gap-1.5 md:gap-3 cursor-pointer group hover:opacity-80 transition-all flex-1 justify-center">
              <div className="w-6 h-6 md:w-10 md:h-10 rounded-full border-2 border-brandDark flex items-center justify-center p-1 group-hover:scale-110 transition-transform">
                <tab.icon className="text-brandDark text-xs md:text-xl origin-center -rotate-45" />
              </div>
              <span className="font-bold text-brandDark text-[11px] md:text-lg tracking-tight whitespace-nowrap">{tab.label}</span>
            </div>
            {idx < 2 && (
              <div className="h-6 md:h-8 border-l-2 border-dashed border-brandDark/20 mx-1.5"></div>
            )}
          </React.Fragment>
        ))}
      </div>

      {/* Main Search Bar */}
      <div className="bg-[#00261E] md:rounded-b-[20px] px-0 md:px-3 py-5 shadow-2xl flex flex-col md:flex-row items-stretch gap-[2px] ">
        <div className="flex-1 flex flex-col md:flex-row gap-[2px]">

          {/* Airport */}
          <div className="flex flex-col w-full md:w-[22%] relative group" ref={dropdownRef}>
            <div className="bg-white px-4 pt-2 md:pt-3 h-[35px] md:h-[40px] rounded-t-[20px] md:rounded-tr-none md:rounded-tl-[20px] flex items-start">
              <label className="text-[10px] md:text-sm font-black text-brandDark uppercase tracking-[0.05em]">Airport</label>
            </div>
            <div
              className="bg-[#F2F4F7] px-4 h-[55px] md:h-[70px] md:rounded-bl-[20px] flex items-center gap-3 cursor-pointer hover:bg-white transition-colors"
              onClick={() => setSelectOpen(!selectOpen)}
            >
              <img src={require("../../images/select-airport.png")} alt="Airport" className="w-[27px] h-[27px] object-contain" />
              <span className="text-sm md:text-base font-bold text-brandDark truncate">
                {selectedAirportLabel || "Select airport"}
              </span>
            </div>
            {selectOpen && (
              <div className="absolute top-full left-0 w-full bg-white  rounded-xl shadow-2xl z-[999] border border-gray-100 overflow-hidden animate-fade-in">
                <input
                  type="text"
                  autoFocus
                  placeholder="Search airport..."
                  className="w-full p-3 text-sm border-b border-gray-100 outline-none"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                <div className="max-h-60 overflow-y-auto custom-scrollbar">
                  {filteredAirports.map((a, idx) => (
                    <div
                      key={idx}
                      className="p-3 hover:bg-brandLime/10 hover:text-brandDark cursor-pointer text-sm font-medium border-b border-gray-50 last:border-0 transition-colors"
                      onClick={() => handleAirportSelect(a)}
                    >
                      {a.label}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Drop-off Date and Time: grouped together side-by-side on mobile, contents on desktop */}
          <div className="w-full flex gap-[2px] md:contents">
            {/* Drop-off Date */}
            <div className="flex flex-col w-[58%] sm:w-[60%] md:w-[17%]">
              <div className="bg-white px-4 pt-2 md:pt-3 h-[35px] md:h-[40px] flex items-start">
                <label className="text-[10px] md:text-sm font-black text-brandDark uppercase tracking-[0.05em] truncate">Drop-off Date</label>
              </div>
              <div className="bg-[#F2F4F7] px-4 h-[55px] md:h-[70px] flex items-center gap-3">
                <img
                  src={dateIcon}
                  alt="Date"
                  className="w-[27px] h-[27px] object-contain cursor-pointer"
                  onClick={() => pickerRef.current?.setOpen(true)}
                />
                <DatePicker
                  selected={moment(departure).toDate()}
                  onChange={setDepartureDate}
                  minDate={moment().toDate()}
                  dateFormat="dd MMM yyyy"
                  className="text-sm md:text-base font-bold w-full outline-none bg-transparent text-brandDark"
                  ref={pickerRef}
                />
              </div>
            </div>

            {/* Drop-off Time */}
            <div className="flex flex-col w-[42%] sm:w-[40%] md:w-[12%]">
              <div className="bg-white px-4 pt-2 md:pt-3 h-[35px] md:h-[40px] flex items-start">
                <label className="text-[10px] md:text-sm font-black text-brandDark uppercase tracking-[0.05em]">Time</label>
              </div>
              <div className="bg-[#F2F4F7] px-4 h-[55px] md:h-[70px] flex items-center gap-3">
                <img
                  src={timeIcon}
                  alt="Time"
                  className="w-[27px] h-[27px] object-contain pointer-events-none"
                />
                <select
                  className="text-sm md:text-base font-bold w-full outline-none bg-transparent cursor-pointer text-brandDark appearance-none"
                  value={moment(departure).format("HH:mm")}
                  onChange={(e) => setTime(e.target.value, "departure")}
                >
                  {times.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
            </div>
          </div>

          {/* Pick-up Date and Time: grouped together side-by-side on mobile, contents on desktop */}
          <div className="w-full flex gap-[2px] md:contents">
            {/* Pick-up Date */}
            <div className="flex flex-col w-[58%] sm:w-[60%] md:w-[17%]">
              <div className="bg-white px-4 pt-2 md:pt-3 h-[35px] md:h-[40px] flex items-start text-nowrap">
                <label className="text-[10px] md:text-sm font-black text-brandDark uppercase tracking-[0.05em] truncate">Pick-up Date</label>
              </div>
              <div className="bg-[#F2F4F7] px-4 h-[55px] md:h-[70px] flex items-center gap-3">
                <img
                  src={dateIcon}
                  alt="Date"
                  className="w-[27px] h-[27px] object-contain cursor-pointer"
                  onClick={() => arrivalPickerRef.current?.setOpen(true)}
                />
                <DatePicker
                  selected={moment(arrival).toDate()}
                  onChange={setArrivalDate}
                  minDate={moment(departure).toDate()}
                  dateFormat="dd MMM yyyy"
                  className="text-sm md:text-base font-bold w-full outline-none bg-transparent text-brandDark"
                  ref={arrivalPickerRef}
                />
              </div>
            </div>

            {/* Pick-up Time */}
            <div className="flex flex-col w-[42%] sm:w-[40%] md:w-[12%]">
              <div className="bg-white px-4 pt-2 md:pt-3 h-[35px] md:h-[40px] flex items-start">
                <label className="text-[10px] md:text-sm font-black text-brandDark uppercase tracking-[0.05em]">Time</label>
              </div>
              <div className="bg-[#F2F4F7] px-4 h-[55px] md:h-[70px] flex items-center gap-3">
                <img
                  src={timeIcon}
                  alt="Time"
                  className="w-[27px] h-[27px] object-contain pointer-events-none"
                />
                <select
                  className="text-sm md:text-base font-bold w-full outline-none bg-transparent cursor-pointer text-brandDark appearance-none"
                  value={moment(arrival).format("HH:mm")}
                  onChange={(e) => setTime(e.target.value, "arrival")}
                >
                  {times.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
            </div>
          </div>

          {/* Promo Code */}
          <div className="flex flex-col w-full md:w-[20%]">
            <div className="bg-white px-4 pt-2 md:pt-3 h-[35px] md:h-[40px] flex items-start md:rounded-tr-[20px]">
              <label className="text-[10px] md:text-sm font-black text-brandDark uppercase tracking-[0.05em]">Promo Code</label>
            </div>
            <div className="bg-[#F2F4F7] px-4 h-[55px] md:h-[70px] flex items-center gap-3 rounded-b-[20px] md:rounded-bl-none md:rounded-br-[20px]">
              <img
                src={promoIcon}
                alt="Promo Code"
                className="w-[27px] h-[27px] object-contain"
              />
              <input
                type="text"
                placeholder="Promo Code"
                className="text-sm md:text-base font-bold w-full outline-none placeholder:text-gray-300 text-brandDark bg-transparent"
                value={promocode}
                onChange={(e) => setPromoCode(e.target.value)}
              />
            </div>
          </div>

        </div>

        {/* Get a Quote Button */}
        <button
          onClick={handleSubmit}
          className="bg-brandLime hover:bg-[#084A3D] hover:border-[#084A3D] transition-all duration-300 rounded-[18px] py-3 px-10 flex flex-row md:flex-col items-center justify-center gap-2 md:gap-0 shadow-2xl group border-2 border-brandLime mx-1 h-[55px] md:h-auto"
        >
          <img
            src={searchIcon}
            alt="Search"
            className="size-5 md:size-14 object-contain group-hover:scale-110 group-hover:brightness-0 group-hover:invert transition-all duration-300"
          />
          <span className="font-black text-brandDark group-hover:text-white text-sm md:text-lg whitespace-nowrap uppercase tracking-tight transition-colors duration-300">Get Quote</span>
        </button>
      </div>

      {validation && (
        <div className="mt-6 bg-red-500/90 text-white p-4 rounded-xl text-center font-bold shadow-lg animate-bounce max-w-md mx-auto">
          {validation}
        </div>
      )}
    </div>
  );
}

